import React, { useEffect, useState, useMemo } from 'react';
import { getAllSavedEmails } from '../api/api';
import { toast } from 'sonner';
import { Copy, CheckCircle2, Search, Download, Loader2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const PAGE_SIZE = 25;

export default function VerifiedEmails() {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showRisky, setShowRisky] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await getAllSavedEmails({ source: 'email-finder,extension', emailStatus: 'Verified,Likely Valid,Risky' });
            setContacts(res.data.data || []);
        } catch (err) {
            toast.error('Veriler yüklenemedi: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Flatten: her contact'ın foundEmails'ini ayrı satırlara çevir
    const verifiedList = useMemo(() => {
        const rows = [];
        contacts.forEach(contact => {
            const name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.name || '—';
            const company = contact.companyNameForEmails || contact.company || '';
            const title = contact.title || '';

            if (contact.foundEmails && contact.foundEmails.length > 0) {
                contact.foundEmails.forEach(fe => {
                    if (fe.status === 'Verified' || fe.status === 'Likely Valid' || (showRisky && fe.status === 'Risky')) {
                        rows.push({ 
                            email: fe.email, 
                            status: fe.status, 
                            name, 
                            company, 
                            title, 
                            contactId: contact._id,
                            confidence: contact.confidenceScore || 0 
                        });
                    }
                });
            } else if (contact.email && (contact.emailStatus === 'Verified' || contact.emailStatus === 'Likely Valid')) {
                rows.push({ 
                    email: contact.email, 
                    status: contact.emailStatus, 
                    name, 
                    company, 
                    title, 
                    contactId: contact._id,
                    confidence: contact.confidenceScore || 0 
                });
            } else if (showRisky && contact.email && contact.emailStatus === 'Risky') {
                rows.push({ 
                    email: contact.email, 
                    status: 'Risky', 
                    name, 
                    company, 
                    title, 
                    contactId: contact._id,
                    confidence: contact.confidenceScore || 0 
                });
            }
        });
        return rows;
    }, [contacts, showRisky]);

    const filteredList = useMemo(() => {
        if (!search.trim()) return verifiedList;
        const q = search.toLowerCase();
        return verifiedList.filter(r =>
            r.email.toLowerCase().includes(q) ||
            r.name.toLowerCase().includes(q) ||
            r.company.toLowerCase().includes(q)
        );
    }, [verifiedList, search]);

    // Pagination
    const totalPages = Math.max(1, Math.ceil(filteredList.length / PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const paginatedList = filteredList.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    // Reset page on search/filter change
    useEffect(() => { setCurrentPage(1); }, [search, showRisky]);

    const copyEmail = (email) => {
        navigator.clipboard.writeText(email);
        toast.success('Kopyalandı!');
    };

    const copyAll = () => {
        const emails = filteredList.map(r => r.email).join('\n');
        navigator.clipboard.writeText(emails);
        toast.success(`${filteredList.length} email kopyalandı!`);
    };

    const exportCsv = () => {
        const header = 'Email,Kişi,Şirket,Unvan,Durum';
        const lines = filteredList.map(r =>
            `"${r.email}","${r.name}","${r.company}","${r.title}","${r.status}"`
        );
        const csv = [header, ...lines].join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `verified_emails_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('CSV indirildi!');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-5xl pt-12 mx-auto">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                    <CheckCircle2 className="h-6 w-6 text-green-500" />
                    Doğrulanmış E-postalar
                </h1>
                <p className="text-muted-foreground text-sm mt-1">
                    Email Finder ile bulunan ve SMTP ile doğrulanmış e-posta adresleri
                </p>
            </div>

            {/* Stats + Actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-3 py-1.5 rounded-lg text-sm font-medium">
                        <CheckCircle2 className="h-4 w-4" />
                        {verifiedList.filter(r => r.status === 'Verified').length} Doğrulanmış
                    </div>
                    <button
                        onClick={() => setShowRisky(!showRisky)}
                        className={cn(
                            "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors",
                            showRisky
                                ? "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                        )}
                    >
                        <AlertTriangle className="h-4 w-4" />
                        {showRisky ? 'Riskli Dahil' : 'Riskli Göster'}
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Email, isim veya şirket ara..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-8 pr-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring w-64"
                        />
                    </div>
                    <Button variant="outline" size="sm" onClick={copyAll} className="h-8">
                        <Copy className="h-3.5 w-3.5 mr-1.5" />
                        Tümünü Kopyala
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportCsv} className="h-8">
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        CSV
                    </Button>
                </div>
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="sticky top-0 bg-background/95 backdrop-blur z-10 border-b">
                                <tr>
                                    <th className="h-10 px-4 font-medium text-muted-foreground w-8">#</th>
                                    <th className="h-10 px-4 font-medium text-muted-foreground">E-posta</th>
                                    <th className="h-10 px-4 font-medium text-muted-foreground">Kişi</th>
                                    <th className="h-10 px-4 font-medium text-muted-foreground">Şirket</th>
                                    <th className="h-10 px-4 font-medium text-muted-foreground">Unvan</th>
                                    <th className="h-10 px-4 font-medium text-muted-foreground">Güven</th>
                                    <th className="h-10 px-4 font-medium text-muted-foreground">Durum</th>
                                    <th className="h-10 px-4 font-medium text-muted-foreground w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedList.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-12 text-muted-foreground">
                                            {search ? 'Arama sonucu bulunamadı' : 'Henüz doğrulanmış email yok'}
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedList.map((row, i) => (
                                        <tr key={`${row.contactId}-${row.email}`}
                                            className="border-b transition-colors hover:bg-muted/50 group">
                                            <td className="p-3 px-4 text-muted-foreground text-xs">
                                                {(safePage - 1) * PAGE_SIZE + i + 1}
                                            </td>
                                            <td className="p-3 px-4 font-mono text-xs font-medium">
                                                {row.email}
                                            </td>
                                            <td className="p-3 px-4 font-medium">{row.name}</td>
                                            <td className="p-3 px-4 text-muted-foreground">{row.company}</td>
                                            <td className="p-3 px-4 text-muted-foreground text-xs max-w-[160px] truncate" title={row.title}>
                                                {row.title}
                                            </td>
                                            <td className="p-3 px-4">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                                                        <div className="h-full bg-blue-500" style={{ width: `${row.confidence}%` }}></div>
                                                    </div>
                                                    <span className="text-[10px] text-muted-foreground">%{row.confidence}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 px-4">
                                                <span className={cn(
                                                    "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide",
                                                    row.status === 'Verified'
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                )}>
                                                    {row.status === 'Verified' ? '✅ Doğrulanmış' : '⚠️ Riskli'}
                                                </span>
                                            </td>
                                            <td className="p-3 px-4">
                                                <Button variant="ghost" size="sm"
                                                    className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    onClick={() => copyEmail(row.email)} title="Kopyala">
                                                    <Copy className="h-3.5 w-3.5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {filteredList.length > 0 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30">
                            <span className="text-xs text-muted-foreground">
                                Toplam {filteredList.length} sonuç · Sayfa {safePage}/{totalPages}
                            </span>
                            <div className="flex items-center gap-1">
                                <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                                    disabled={safePage <= 1}
                                    onClick={() => setCurrentPage(1)} title="İlk sayfa">
                                    <span className="text-xs">1</span>
                                </Button>
                                <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                                    disabled={safePage <= 1}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
                                    <ChevronLeft className="h-3.5 w-3.5" />
                                </Button>

                                {/* Page numbers */}
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
                                    .reduce((acc, p, idx, arr) => {
                                        if (idx > 0 && p - arr[idx - 1] > 1) {
                                            acc.push('...');
                                        }
                                        acc.push(p);
                                        return acc;
                                    }, [])
                                    .map((p, idx) => (
                                        p === '...' ? (
                                            <span key={`dots-${idx}`} className="text-xs text-muted-foreground px-1">…</span>
                                        ) : (
                                            <Button key={p} variant={p === safePage ? "default" : "outline"}
                                                size="sm" className="h-7 w-7 p-0 text-xs"
                                                onClick={() => setCurrentPage(p)}>
                                                {p}
                                            </Button>
                                        )
                                    ))}

                                <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                                    disabled={safePage >= totalPages}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
                                    <ChevronRight className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                                    disabled={safePage >= totalPages}
                                    onClick={() => setCurrentPage(totalPages)} title="Son sayfa">
                                    <span className="text-xs">{totalPages}</span>
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
